import { TrainIdentifier } from "@vygruppen/vy-train-map";
import {
  validateTypeAndDescription,
  validateTypeAndDescriptionNullable,
} from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/transformers";
import {
  RouteSection,
  SingleTrainFormSchema,
  TrainFormSchema,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/formSchema";
import {
  BaseRequest,
  RouteSectionRequest,
  TrainInfoRequest,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/requestSchema";
import {
  TrainInformationDetailedResponse,
  trainResponseIsTrainCustomResponse,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/responseSchema";
import { TrainEventTypeEnum } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/types/trainEventTypeEnum";
import { DefaultValues } from "react-hook-form";
import {
  getDefaultPrognosis,
  getPrognosisTime,
  validatePrognosis,
} from "features/CenterContent/shared/operationalInformation/prognosisUtils";
import { UserRole } from "shared/types/roles";

const toRouteSectionRequest = (
  routeSection: RouteSection,
): RouteSectionRequest => {
  if (routeSection.type === "AT_STOP") {
    return { fromStop: routeSection.fromStop };
  }
  return { fromStop: routeSection.fromStop, toStop: routeSection.toStop };
};

export const singleTrainFormToRequestBody = (
  formState: TrainFormSchema,
  train: TrainIdentifier,
): TrainInfoRequest => {
  const baseRequestBody: BaseRequest = {
    trainIdentifier: train,
    incidentIds: formState.incidentId ? [formState.incidentId] : undefined,
    reason: validateTypeAndDescription(formState.reason),
    action: validateTypeAndDescriptionNullable(formState.action),
    prognosis: validatePrognosis(
      formState.prognosisType ?? null,
      formState.prognosisTime,
    ),
  };

  const dropsLogText =
    formState?.type &&
    formState.type !== TrainEventTypeEnum.TRAIN_CUSTOM &&
    formState?.dropsLogText?.enabled
      ? formState.dropsLogText.texts
      : undefined;

  switch (formState?.type) {
    case TrainEventTypeEnum.TRAIN_STOPPED:
      return {
        ...baseRequestBody,
        trainInformationType: formState.type,
        currentRouteSection: toRouteSectionRequest(formState.trainRouteSection),
        dropsLogText,
      };
    case TrainEventTypeEnum.TRAIN_CANCELLED:
      return {
        ...baseRequestBody,
        trainInformationType: formState.type,
        affectedStops: formState.affectedStops,
        dropsLogText,
      };
    case TrainEventTypeEnum.TRAIN_CAPACITY_REDUCED:
      return {
        ...baseRequestBody,
        trainInformationType: formState.type,
        affectedStops: formState.affectedStops,
        oldCapacity: formState.oldCapacity,
        newCapacity: formState.newCapacity,
        oldCapacityUnit: formState.newCapacityUnit,
        newCapacityUnit: formState.oldCapacityUnit,
        dropsLogText,
      };
    case TrainEventTypeEnum.TRAIN_CAPACITY_INCREASED:
      return {
        ...baseRequestBody,
        trainInformationType: formState.type,
        affectedStops: formState.affectedStops,
        oldCapacity: formState.oldCapacity,
        newCapacity: formState.newCapacity,
        oldCapacityUnit: formState.newCapacityUnit,
        newCapacityUnit: formState.oldCapacityUnit,
        dropsLogText,
      };
    case TrainEventTypeEnum.TRAIN_DELAYED:
      return {
        ...baseRequestBody,
        trainInformationType: formState.type,
        affectedStops: formState.affectedStops,
        delayInMinutes: formState.delayInMinutes,
        dropsLogText,
      };
    case TrainEventTypeEnum.TRAIN_DELAY_EXPECTED:
      return {
        ...baseRequestBody,
        trainInformationType: formState.type,
        affectedStops: formState.affectedStops,
        delayInMinutes: formState.delayInMinutes,
        dropsLogText,
      };
    case TrainEventTypeEnum.TRAIN_LATE_TO_TRACK:
      return {
        ...baseRequestBody,
        trainInformationType: formState.type,
        affectedStops: formState.affectedStops,
        minutesLate: formState.minutesLate,
        dropsLogText,
      };
    case TrainEventTypeEnum.TRAIN_NOT_STOPPING_AT_STATION:
      return {
        ...baseRequestBody,
        trainInformationType: formState.type,
        dropsLogText,
        affectedStops: formState.skippedStops,
      };
    case TrainEventTypeEnum.TRAIN_MISSING_PRODUCT:
      return {
        ...baseRequestBody,
        trainInformationType: formState.type,
        affectedStops: formState.affectedStops,
        products: formState.products,
        coverage: formState.coverage,
        dropsLogText,
      };
    case TrainEventTypeEnum.TRAIN_STOPPING_EXTRA_AT_STATION:
      return {
        ...baseRequestBody,
        trainInformationType: formState.type,
        dropsLogText,
        affectedStops: formState.extraStops,
      };
    case TrainEventTypeEnum.TRAIN_CLOSED_SET:
      return {
        ...baseRequestBody,
        trainInformationType: formState.type,
        affectedStops: formState.affectedStops,
        dropsLogText,
      };
    case TrainEventTypeEnum.TRAIN_CUSTOM:
      return {
        ...baseRequestBody,
        trainInformationType: formState.type,
        affectedStops: formState.affectedStops,
        severity: formState.severity,
        distributions: formState.distributions,
      };
    case TrainEventTypeEnum.TRAIN_CHANGED_ROUTE:
      return {
        ...baseRequestBody,
        trainInformationType: formState.type,
        affectedStops: formState.affectedStops,
        newRoute: formState.newRoute,
        cancelledStops: formState.cancelledStops,
        originalRouteName: formState.originalRouteName,
        newRouteName: formState.newRouteName,
        dropsLogText,
      };
  }
};

export function trainDetailedResponseToForm(
  trainResponse: TrainInformationDetailedResponse,
): TrainFormSchema {
  const {
    type: responseType,
    dropsLogText: responseDropsLogText,
    incidentIds: responseIncidentIds,
    prognosis,
    ...response
  } = trainResponse;
  // Opinfo has different types for TRAIN_GENERAL_INFO and _WARNING, but for UX reasons
  // the modal form handles them as the same event with severity as a different field.
  // Therefore, we have to consolidate the type here to be able to parse those events.
  const type: TrainEventTypeEnum = trainResponseIsTrainCustomResponse(
    trainResponse,
  )
    ? TrainEventTypeEnum.TRAIN_CUSTOM
    : responseType;

  // The zod form has an extra state field to control whether to show the drops log
  // text field, so we have to infer that state here
  const dropsLogText = trainResponseIsTrainCustomResponse(trainResponse)
    ? { enabled: false, texts: { NOB: "" } }
    : {
        enabled: !!responseDropsLogText?.NOB,
        texts: responseDropsLogText ?? { NOB: "" },
      };

  // Translate from incidentIds to incidentId
  const incidentId = responseIncidentIds?.length
    ? responseIncidentIds[0]
    : undefined;

  // Ideally this response should be parsed using trainFormSchema.parse,
  // but as long as event can be used to create or edit event
  // we can't, because some fields that are optional in event are required
  // in our schema, i.e. reason and affectedStops
  return {
    ...response,
    type,
    dropsLogText,
    prognosisType: prognosis ? { type: prognosis.type } : null,
    prognosisTime: getPrognosisTime(prognosis),
    incidentId,
  } as TrainFormSchema;
}

export const baseDefaultFormSchema = (): DefaultValues<TrainFormSchema> => ({
  type: undefined,
  reason: { type: "" },
  action: { type: "" },
  dropsLogText: { enabled: false, texts: { NOB: "" } },
  ...getDefaultPrognosis(),
});

export const defaultFormSchema = (
  role: UserRole,
): DefaultValues<SingleTrainFormSchema> => {
  if (role === UserRole.lokleder1 || role === UserRole.lokleder2) {
    return {
      trainForm: {
        type: TrainEventTypeEnum.TRAIN_STOPPED,
        reason: { type: "TECHNICAL_FAULT" },
        action: { type: "" },
        dropsLogText: { enabled: true, texts: { NOB: "" } },
        ...getDefaultPrognosis(),
      },
    };
  }
  return { trainForm: baseDefaultFormSchema() };
};
